/* import __COLOCATED_TEMPLATE__ from './conversation-page-command-k-actions.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxState from 'embercom/services/inbox-state';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { isEmpty } from '@ember/utils';
import { type DurationObject, DurationType } from 'embercom/objects/inbox/duration';
import { isPartTaggedWith } from 'embercom/objects/inbox/taggable-part';
import Tag, { type TagActionType } from 'embercom/objects/inbox/tag';
import type WorkflowConnectorAction from 'embercom/objects/inbox/workflow-connector-action';
import type WorkflowDetails from 'embercom/objects/inbox/workflow-details';
import type SidebarSection from 'embercom/objects/inbox/sidebar-section';
import { type SupportedSidebarSection } from 'embercom/services/inbox-sidebar-service';
import type InboxSidebarService from 'embercom/services/inbox-sidebar-service';
import { taskFor } from 'ember-concurrency-ts';
import type UserComment from 'embercom/objects/inbox/renderable/user-comment';
import { type ConversationResource } from './conversation-resource';
import { TicketCategory, type TicketType } from 'embercom/objects/inbox/ticket';
import type LinkedTicketSummary from 'embercom/objects/inbox/linked-ticket-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type LinkedTicketsResource from './linked-tickets-resource';
import type IntlService from 'embercom/services/intl';
import type PermissionsModal from 'embercom/services/permissions-modal';
import { Channel, CHAT_CHANNELS } from 'embercom/models/data/inbox/channels';
import { type Block } from '@intercom/interblocks.ts';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

interface Args {
  conversationResource: ConversationResource;
  setTicketTypeToLinkToConversation: (ticketTypeId?: number) => void;
  setTicketTypeToConvertToTicket: (ticketTypeId?: number) => void;
  closeTicketCreationPanel?: () => void;
  closeConversation: () => void;
  linkedTicketsResource: LinkedTicketsResource;
}

interface Signature {
  Args: Args;
}

export default class ConversationPageCommandKActionsComponent extends Component<Signature> {
  @service declare inboxState: InboxState;
  @service declare session: Session;
  @service declare inboxSidebarService: InboxSidebarService;
  @service declare intercomEventService: any;
  @service declare inboxApi: InboxApi;
  @service declare intl: IntlService;
  @service declare permissionsModal: PermissionsModal;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked tagActionType: TagActionType = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
  }

  get conversation(): Conversation {
    return this.args.conversationResource.conversation;
  }

  get lastUserCommentContext() {
    return {
      conversationPart: this.conversation.lastUserComment,
      tags: (this.conversation.lastUserComment?.renderableData as UserComment)?.tags ?? [],
    };
  }

  get firstUserCommentContext() {
    return {
      conversationPart: this.conversation.firstUserComment,
      tags: (this.conversation.firstUserComment?.renderableData as UserComment)?.tags ?? [],
    };
  }

  @action updateTagsForConversationPart(tag: Tag, context: any) {
    this.optimisticUpdateTagsForConversationPart(tag, context);
  }

  @action legacyUpdateTagsForConversationPart(tag: Tag, context: any) {
    let conversationPart = context.conversationPart;
    let partIsTagged = isPartTaggedWith(conversationPart, tag);
    let newTags;
    let { tags: existingTags }: { tags: Array<Tag> } = conversationPart.renderableData;

    if (partIsTagged) {
      newTags = existingTags.filter((t) => t.id !== tag.id);
    } else {
      newTags = [...existingTags, new Tag(tag.id, tag.name, this.session.teammate)];
    }

    this.setTagActionType(partIsTagged ? 'removed' : 'added');
    taskFor(this.inboxState.updateTags).perform(this.conversation, conversationPart, newTags);
  }

  @action optimisticUpdateTagsForConversationPart(tag: Tag, context: any) {
    let conversationPart = context.conversationPart;
    let partIsTagged = isPartTaggedWith(conversationPart, tag);

    this.setTagActionType(partIsTagged ? 'removed' : 'added');

    if (partIsTagged) {
      this.inboxState.removeTag(this.conversation, conversationPart, tag);
    } else {
      this.inboxState.addTag(this.conversation, conversationPart, tag);
    }
  }

  @action triggerWorkflowConnectorAction(action: WorkflowConnectorAction) {
    if (!this.session.teammate.permissions.canUseDataConnectors) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t('inbox.common.permissions-label.can-use-data-connectors'),
      });
      return;
    }

    taskFor(this.inboxState.triggerWorkflowConnectorAction).perform(
      this.conversation,
      null,
      action,
    );
  }

  @action triggerWorkflow(workflowDetails: WorkflowDetails) {
    taskFor(this.inboxState.triggerWorkflow).perform(this.conversation, workflowDetails);
  }

  @action triggerFinAiAgent() {
    taskFor(this.inboxState.triggerFinAiAgent).perform(this.conversation);
  }

  get descriptors() {
    return this.session.workspace.conversationAttributeDescriptors ?? [];
  }

  get hasSharedTaskTickets() {
    if (!this.args.linkedTicketsResource?.tickets) {
      return false;
    }

    return this.args.linkedTicketsResource.tickets
      .filter((ticket: LinkedTicketSummary) => ticket.category === TicketCategory.Task)
      .some((taskTickets: LinkedTicketSummary) => taskTickets.visibleToUser);
  }

  /**
   * Mirrors conversation-reply-composer.ts canAddTicket but without replyPaneIsActive
   * (this component is conversation-level, not pane-level). The Ember composer's version
   * also checks replyPaneIsActive since it only shows the action on the reply pane.
   *
   * Channel checks match: isChatChannel || isSlackConversation || isHostedTicketFormChannel,
   * where isHostedTicketFormChannel === isDiscordConversation.
   */
  get canAddTicket() {
    let replyChannel = this.conversation.channel.replyChannel;
    let isSupportedChannel =
      CHAT_CHANNELS.includes(replyChannel) ||
      replyChannel === Channel.Slack ||
      replyChannel === Channel.Discord;
    return (
      isSupportedChannel &&
      !this.conversation.isTicket &&
      Boolean(this.conversation.canShareLinkedTicket)
    );
  }

  @action insertTicketCardBlock(ticketType: TicketType) {
    let block = {
      icon_url: '',
      state: 'interactive',
      text: this.intl.t('inbox.conversation-reply-composer.ticket-card-block-text'),
      ticket_type_id: ticketType.id,
      ticket_type_title: ticketType.name,
      title: this.intl.t('inbox.conversation-reply-composer.ticket-card-block-title'),
      type: 'createTicketCard',
    } as Block;
    this.reactTeammateAppIsland.dispatchTeammateAppEvent('composer:ticket-card-selected', {
      conversationId: this.conversation.id,
      block,
    });
  }

  get canConvertToTicket() {
    return !this.conversation.isTicket && !this.hasSharedTaskTickets;
  }

  get convertToTicket() {
    return this.args.setTicketTypeToConvertToTicket;
  }

  commandKSnoozeTrackingDataFromSelectValue(duration: DurationObject) {
    return {
      snoozed_until: DurationType[duration.type],
    };
  }

  @action jumpToAppDetails(param: SidebarSection) {
    let sectionId = (
      !isEmpty(param.intercomModuleIdentifier) ? param.intercomModuleIdentifier : param.inboxAppId
    ) as SupportedSidebarSection;
    if (sectionId) {
      let waitBeforeScroll = false;
      if (!this.inboxSidebarService.openSidebarSections.includes(sectionId)) {
        this.inboxSidebarService.onOpenSectionChange(sectionId)(sectionId);
        waitBeforeScroll = true;
      }
      taskFor(this.inboxSidebarService.scrollToSection).perform(sectionId, waitBeforeScroll);
    }
  }

  private checkConversationPermission(): boolean {
    if (!this.session.teammate.permissions.canManageConversationCreation) {
      this.permissionsModal.showModal({
        permissionsLabel: this.intl.t(
          'inbox.conversation-header.create-conversation-permission-label',
        ),
      });
      return false;
    }
    return true;
  }

  @action createSideConversation() {
    if (!this.checkConversationPermission()) {
      return;
    }

    this.inboxSidebarService.openNewSideConversationPanel();
  }

  @action setTicketTypeToLink({ id }: { id: number }) {
    this.args.setTicketTypeToLinkToConversation(id);
  }

  @action setTicketTypeToConvertToTicket({ id }: { id: number }) {
    this.args.setTicketTypeToConvertToTicket(id);
  }

  @action setTagActionType(type: TagActionType) {
    this.tagActionType = type;
  }

  @action togglePriority() {
    this.inboxState.changePriority(this.conversation, !this.conversation.priority);
  }

  @action onSnoozeSelect(duration: DurationObject) {
    this.args.conversationResource.snoozeConversation(duration);

    this.args.closeTicketCreationPanel && this.args.closeTicketCreationPanel();
  }

  @action openCopilot() {
    this.inboxSidebarService.openCopilot();
  }

  get togglePriorityTrackingData() {
    return {
      action: 'changed',
      object: 'priority',
      section: 'conversation_details',
      marked_as_priority: this.conversation.priority,
      inbox_type: this.inboxState.activeInbox?.type,
      layout_type: this.inboxState.activeConversationsView,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationPageCommandKActions': typeof ConversationPageCommandKActionsComponent;
    'inbox2/conversation-page-command-k-actions': typeof ConversationPageCommandKActionsComponent;
  }
}
